// Deutsche Labels zum Audit-Log. Die Codes selbst stehen im Server-Katalog
// (utils/auditActions.js); tests/unit/auditCatalog.test.js lädt diese Datei
// in Node und prüft, dass jeder Server-Code hier ein Label hat. Deshalb:
// reines JS, kein JSX, keine Imports.
//
// Ein neuer Code ist eine Zeile in der passenden Map. Unbekannte Codes
// erscheinen roh, statt zu verschwinden.

export const AUDIT_ACTION_LABELS = Object.freeze({
  // Anmeldung
  'auth.login': 'Anmeldung',
  'auth.logout': 'Abmeldung',
  'auth.reauth': 'Passwortbestätigung',
  // Registrierung und Konto
  'account.register': 'Registrierung',
  'account.email_verified': 'E-Mail bestätigt',
  'account.invitation_accepted': 'Firmeneinladung angenommen',
  'account.email_change_requested': 'E-Mail-Änderung beantragt',
  'account.email_changed': 'Login-E-Mail geändert',
  'account.email_change_cancelled': 'E-Mail-Änderung abgebrochen',
  // Passwort
  'password.reset_requested': 'Passwort-Reset angefordert',
  'password.reset': 'Passwort zurückgesetzt',
  // Zwei-Faktor
  'mfa.totp_enabled': 'Authenticator-App aktiviert',
  'mfa.totp_disabled': 'Authenticator-App deaktiviert',
  'mfa.email_otp_enabled': 'E-Mail-Code aktiviert',
  'mfa.email_otp_disabled': 'E-Mail-Code deaktiviert',
  'mfa.passkey_added': 'Passkey hinzugefügt',
  'mfa.passkey_removed': 'Passkey entfernt',
  'mfa.recovery_codes_regenerated': 'Recovery-Codes neu erzeugt',
  // Karten
  'card.created': 'Karte angelegt',
  'card.claimed': 'Karte beansprucht',
  'card.owner_changed': 'Kartenbesitzer geändert',
  'card.blocked': 'Karte gesperrt',
  'card.unblocked': 'Karte entsperrt',
  'card.updated': 'Karte bearbeitet',
  'card.deleted': 'Karte gelöscht',
  // Benutzer
  'user.created': 'Benutzer angelegt',
  'user.updated': 'Benutzer bearbeitet',
  'user.deleted': 'Benutzer gelöscht',
  // Firmen und Mitglieder
  'company.created': 'Firma angelegt',
  'company.updated': 'Firma bearbeitet',
  'company.suspended': 'Firma suspendiert',
  'company.deleted': 'Firma gelöscht',
  'company.member_invited': 'Mitglied eingeladen',
  'company.invitation_cancelled': 'Einladung zurückgezogen',
  'company.member_added': 'Mitglied aufgenommen',
  'company.member_removed': 'Mitglied entfernt',
  'company.member_role_changed': 'Firmenrolle geändert',
  'company.member_updated': 'Mitglied bearbeitet',
});

export const AUDIT_CATEGORY_LABELS = Object.freeze({
  auth: 'Anmeldung',
  account: 'Konto',
  password: 'Passwort',
  mfa: 'Zwei-Faktor',
  card: 'Karten',
  user: 'Benutzer',
  company: 'Firmen',
});

export const AUDIT_OUTCOME_LABELS = Object.freeze({
  success: 'Erfolg',
  failure: 'Fehlgeschlagen',
  denied: 'Verweigert',
});

export const AUDIT_REASON_LABELS = Object.freeze({
  unknown_email: 'Unbekannte E-Mail',
  wrong_password: 'Falsches Passwort',
  not_verified: 'E-Mail nicht bestätigt',
  not_activated: 'Konto nicht aktiviert',
  invalid_code: 'Ungültiger Code',
  replay: 'Code bereits verwendet',
  locked_out: 'Gesperrt nach 5 Fehlversuchen',
  unknown_passkey: 'Unbekannter Passkey',
  verification_failed: 'Passkey-Prüfung fehlgeschlagen',
  admin_role_requested: 'Admin-Rolle verlangt',
  other_account: 'Einladung für anderes Konto',
  email_taken: 'Adresse bereits vergeben',
  expired: 'Link abgelaufen',
  invalid_token: 'Link ungültig',
  not_company_member: 'Kein Mitglied der Firma',
});

// Schlüssel in `details`.
export const AUDIT_DETAIL_LABELS = Object.freeze({
  method: 'Methode',
  stage: 'Stufe',
  remaining: 'Verbleibende Recovery-Codes',
  via: 'Weg',
  company: 'Firma',
  context: 'Anlass',
  activated: 'Konto aktiviert',
  verified: 'E-Mail dabei bestätigt',
  name: 'Name',
  truncated: 'gekürzt',
  status: 'Status',
  owner: 'Besitzer',
  cards: 'Karten',
  reserve: 'davon Reserve',
  owners: 'Besitzer',
  ownersTotal: 'Besitzer insgesamt',
  blockedCards: 'Gesperrte Karten',
  event: 'Stripe-Ereignis',
  activation: 'Zugang',
  role: 'Rolle',
  existingUser: 'Bestehendes Konto',
  adminUser: 'Erster Firmenadmin',
  deletedCards: 'Gelöschte Karten',
  companyCards: 'davon Firmenkarten',
  cardUrls: 'Karten-URLs',
  detachedMembers: 'Gelöste Mitglieder',
});

// Status von Karten, Benutzern und Firmen, wie im übrigen Admin (StatusBadge).
const STATUS_LABELS = Object.freeze({
  active: 'Aktiv',
  blocked: 'Blockiert',
  demo: 'Demo',
  unclaimed: 'Nicht beansprucht',
  ordered: 'Bestellt',
  pending: 'Ausstehend',
  suspended: 'Suspendiert',
});

const COMPANY_ROLE_LABELS = Object.freeze({
  member: 'Mitglied',
  companyAdmin: 'Firmenadmin',
});

// Werte einzelner `details`-Schlüssel.
export const AUDIT_DETAIL_VALUE_LABELS = Object.freeze({
  method: Object.freeze({
    password: 'Passwort',
    passkey: 'Passkey',
    'mfa:totp': 'Authenticator-App',
    'mfa:emailOtp': 'E-Mail-Code',
    'mfa:recovery': 'Recovery-Code',
    'mfa:webauthn': 'Passkey (2. Faktor)',
  }),
  stage: Object.freeze({
    mfa: 'Zweiter Faktor',
  }),
  via: Object.freeze({
    self: 'selbst',
    invitation: 'Einladung',
    admin: 'Admin-Bereich',
    company: 'Firmenverwaltung',
  }),
  status: STATUS_LABELS,
  role: COMPANY_ROLE_LABELS,
  activation: Object.freeze({
    password: 'Startpasswort gesetzt',
    mail: 'Aktivierungslink per E-Mail',
    none: 'Ohne Login (verwaltetes Profil)',
  }),
  event: Object.freeze({
    'invoice.voided': 'Rechnung storniert',
    'invoice.marked_uncollectible': 'Rechnung uneinbringlich',
  }),
  // Passwortbestätigung (auth.reauth): wofür das Passwort verlangt wurde.
  context: Object.freeze({
    'account.email_change': 'E-Mail-Änderung',
    'mfa.totp_setup': 'Authenticator-App einrichten',
    'mfa.totp_disable': 'Authenticator-App deaktivieren',
    'mfa.email_otp_enable': 'E-Mail-Code aktivieren',
    'mfa.email_otp_disable': 'E-Mail-Code deaktivieren',
    'mfa.passkey_register': 'Passkey hinzufügen',
    'mfa.passkey_remove': 'Passkey entfernen',
    'mfa.recovery_codes': 'Recovery-Codes neu erzeugen',
  }),
});

// Feldnamen in `changes`.
export const AUDIT_FIELD_LABELS = Object.freeze({
  email: 'E-Mail',
  owner: 'Besitzer',
  status: 'Status',
  companyBlockedFrom: 'Status vor Firmensperre',
  url: 'URL',
  company: 'Firma',
  orderId: 'Bestellung',
  designId: 'Design',
  name: 'Name',
  graphicFront: 'Grafik vorne',
  graphicBack: 'Grafik hinten',
  nfcEncoded: 'NFC kodiert',
  firstName: 'Vorname',
  lastName: 'Nachname',
  title: 'Titel',
  organization: 'Organisation',
  isVerified: 'E-Mail bestätigt',
  companyRole: 'Firmenrolle',
  website: 'Website',
  address: 'Adresse',
  logo: 'Logo',
  stripeCustomerId: 'Stripe-Kunde',
  stripeSubscriptionId: 'Stripe-Abo',
  subscriptionStatus: 'Abo-Status',
  paidUsers: 'Bezahlte Plätze',
});

// Werte einzelner Felder in `changes`.
const AUDIT_FIELD_VALUE_LABELS = Object.freeze({
  status: STATUS_LABELS,
  companyBlockedFrom: STATUS_LABELS,
  companyRole: COMPANY_ROLE_LABELS,
});

export const AUDIT_ACTOR_KIND_LABELS = Object.freeze({
  user: 'Benutzer',
  anonymous: 'Anonym',
  system: 'System',
});

export const AUDIT_TARGET_KIND_LABELS = Object.freeze({
  user: 'Benutzer',
  card: 'Karte',
  company: 'Firma',
  order: 'Bestellung',
  invitation: 'Einladung',
});

export const AUDIT_ROLE_LABELS = Object.freeze({
  user: 'Benutzer',
  admin: 'Plattform-Admin',
});

const has = (map, key) =>
  typeof key === 'string' && Object.prototype.hasOwnProperty.call(map, key);

/** Label aus `map`, sonst der rohe Code (leer bleibt leer). */
export const auditLabel = (map, code) =>
  has(map, code) ? map[code] : code ?? '';

export const auditActionLabel = (code) => auditLabel(AUDIT_ACTION_LABELS, code);
export const auditCategoryLabel = (code) =>
  auditLabel(AUDIT_CATEGORY_LABELS, code);
export const auditReasonLabel = (code) => auditLabel(AUDIT_REASON_LABELS, code);
export const auditDetailLabel = (key) => auditLabel(AUDIT_DETAIL_LABELS, key);
export const auditFieldLabel = (field) => auditLabel(AUDIT_FIELD_LABELS, field);

/**
 * Wert als Text: Ja/Nein für Booleans, bekannte Codes über
 * `valueLabels`, Strings und Zahlen roh, Listen kommagetrennt, alles andere
 * als JSON.
 */
export const formatAuditValue = (value, valueLabels = {}) => {
  if (value === undefined || value === null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Ja' : 'Nein';
  if (typeof value === 'string') return auditLabel(valueLabels, value);
  if (typeof value === 'number') return String(value);
  if (
    Array.isArray(value) &&
    value.every((item) => ['string', 'number'].includes(typeof item))
  ) {
    return value.map((item) => formatAuditValue(item, valueLabels)).join(', ');
  }
  return JSON.stringify(value);
};

/** Wert eines Felds in `changes` als Text. */
export const auditFieldValue = (field, value) =>
  formatAuditValue(
    value,
    has(AUDIT_FIELD_VALUE_LABELS, field) ? AUDIT_FIELD_VALUE_LABELS[field] : {}
  );

/** Wert eines `details`-Schlüssels als Text. */
export const auditDetailValue = (key, value) =>
  formatAuditValue(
    value,
    has(AUDIT_DETAIL_VALUE_LABELS, key) ? AUDIT_DETAIL_VALUE_LABELS[key] : {}
  );
